import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { BulkOperation, BulkRequestSelection } from '@/composables/api/useBulkActionApi';

/**
 * The bulk-action run this browser is following, so a page reload does not lose it.
 *
 * It follows `trackListExportStore`. The run id is kept in localStorage. After a reload the
 * bulk bar reads it back, fetches the run, and subscribes again. When the store is empty, the
 * bar asks `GET /api/bulk-action-runs/active` instead, which finds a run started in another tab.
 *
 * There is at most one tracked run. The server refuses a second run while one is queued or running.
 * An action on lists and schedules adds a pending run, which waits for the tracked run to finish.
 * The entry stays after the run finishes, so the report stays reachable until the user
 * dismisses it.
 */
export interface TrackedBulkActionRun {
  runId: number;
  /** Null until execute registers the async operation. */
  operationId: string | null;
  scope: string | null;
  /** The entity slug, for example `custom_playlist_schedule`. */
  entity: string;
  /** Epoch ms. Used to drop an entry whose run the server has certainly pruned. */
  startedAt: number;
  /**
   * Display labels for the values the user picked, by field. They are not sent to the server.
   * The cache patch uses them to redraw a lookup's name, such as a new source bucket.
   */
  valueLabels?: Record<string, string>;
  /** True once the cached search rows were patched for this run, so they are never patched twice. */
  cachePatched?: boolean;
  /**
   * The run that finished just before this one, in the same action on lists and schedules. For
   * example, a delete of 3 lists and 14 schedules deletes the schedules first, then the lists.
   * The report shows both runs.
   */
  previousRun?: { runId: number; entity: string } | null;
}

/**
 * A previewed run that waits for the tracked run to finish, in an action on lists and schedules.
 * The server refuses a second run while one is queued or running, so the list run is executed
 * only once the schedule run is final. It is kept here so a reload still starts it.
 */
export interface PendingBulkActionRun {
  /** The previewed run to execute. */
  runId: number;
  /** The run it waits for. */
  afterRunId: number;
  entity: string;
  /** Kept so the run can be previewed again when its preview has expired. */
  selection: BulkRequestSelection;
  operation: BulkOperation;
  valueLabels?: Record<string, string>;
}

/** Where the persisted store lives in localStorage. */
const STORAGE_KEY = 'bulkActionRunStore';

/**
 * Remove the stored run without touching the in-memory store.
 * `clearTokens` calls it, so a session that ends without a logout still drops the selection.
 */
export function clearPersistedBulkActionRun(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

/** The server prunes runs after 30 days. Past that the run id can only answer 404. */
const RUN_RETENTION_MS = 30 * 24 * 3600 * 1000;

export const useBulkActionRunStore = defineStore(
  'bulkActionRun',
  () => {
    const trackedRun = ref<TrackedBulkActionRun | null>(null);
    const pendingRun = ref<PendingBulkActionRun | null>(null);

    /**
     * Whether a value read back from localStorage can be followed.
     * localStorage is untrusted input. It survives deploys that change this shape.
     */
    function isUsable(candidate: unknown): candidate is TrackedBulkActionRun {
      if (candidate === null || typeof candidate !== 'object') return false;
      const entry = candidate as Partial<TrackedBulkActionRun>;
      return (
        typeof entry.runId === 'number' &&
        Number.isInteger(entry.runId) &&
        entry.runId > 0 &&
        typeof entry.entity === 'string' &&
        entry.entity !== '' &&
        typeof entry.startedAt === 'number' &&
        Number.isFinite(entry.startedAt)
      );
    }

    function isUsablePending(candidate: unknown): candidate is PendingBulkActionRun {
      if (candidate === null || typeof candidate !== 'object') return false;
      const entry = candidate as Partial<PendingBulkActionRun>;
      return (
        Number.isInteger(entry.runId) &&
        Number.isInteger(entry.afterRunId) &&
        typeof entry.entity === 'string' &&
        entry.entity !== '' &&
        entry.selection !== null &&
        typeof entry.selection === 'object' &&
        entry.operation !== null &&
        typeof entry.operation === 'object'
      );
    }

    /** Drop a malformed entry, or one too old for its run to exist. */
    function pruneExpired(now: number = Date.now()): void {
      const entry = trackedRun.value;
      if (entry !== null && (!isUsable(entry) || now - entry.startedAt >= RUN_RETENTION_MS)) {
        trackedRun.value = null;
      }
      // A pending run only makes sense after the run it waits for.
      const pending = pendingRun.value;
      if (pending !== null && (!isUsablePending(pending) || trackedRun.value?.runId !== pending.afterRunId)) {
        pendingRun.value = null;
      }
    }

    function track(entry: TrackedBulkActionRun): void {
      if (!isUsable(entry)) return;
      trackedRun.value = { ...entry };
    }

    /** Record the async operation once execute has registered it. */
    function attachOperation(runId: number, operationId: string | null, scope: string | null): void {
      if (trackedRun.value?.runId !== runId) return;
      trackedRun.value = { ...trackedRun.value, operationId, scope };
    }

    function markCachePatched(runId: number): void {
      if (trackedRun.value?.runId !== runId) return;
      trackedRun.value = { ...trackedRun.value, cachePatched: true };
    }

    function setPending(entry: PendingBulkActionRun): void {
      if (!isUsablePending(entry)) return;
      pendingRun.value = { ...entry };
    }

    function clearPending(): void {
      pendingRun.value = null;
    }

    function forget(): void {
      trackedRun.value = null;
      pendingRun.value = null;
    }

    return {
      trackedRun,
      pendingRun,
      track,
      attachOperation,
      markCachePatched,
      setPending,
      clearPending,
      forget,
      pruneExpired,
    };
  },
  {
    persist: {
      storage: localStorage,
      key: STORAGE_KEY,
      pick: ['trackedRun', 'pendingRun'],
    },
  },
);
